import { BaseView } from "@nolimitcity/slot-game/bin/core/base/BaseView";
import { GameConfig } from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import { StageManager } from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import { MathHelper } from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import { EventHandler } from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import { ReelEngineEvent } from "@nolimitcity/slot-game/bin/core/engines/reel/event/ReelEngineEvent";
import { GameEvent } from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import { ServerEvent } from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import { ParsedInitData } from "@nolimitcity/slot-game/bin/core/server/data/ParsedInitData";
import { ISymbolValues } from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import { Helper } from "@nolimitcity/slot-game/bin/core/utils/Helper";
import { ResizeHelper } from "@nolimitcity/slot-game/bin/core/utils/ResizeHelper";
import { TextStyleOptions } from "@nolimitcity/slot-game/bin/core/text/TextStyleOptions";
import { FontWeight } from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import { ArrayHelper } from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import { TimelineMax } from "gsap";
import { BalanceEvent } from "@nolimitcity/slot-game/bin/core/balance/event/BalanceEvent";
import { StageEvent } from "@nolimitcity/slot-game/bin/core/stage/event/StageEvent";
import { Currency } from "@nolimitcity/slot-game/bin/core/balance/Currency";

export class PayTableEvent{
    public static readonly CLICK:string = "click";
}

interface PayTableData{
    dispObj:PIXI.DisplayObject;
    name?:string;
}

interface PayTableScaling{
    portrait:PIXI.Point;
    landscape:PIXI.Point;
}

export interface MiniPayTableConfig{
    style?:TextStyleOptions;
    orientation_scale?:PayTableScaling;
    showBetValue?:boolean;
    xtraArea?:number;
    bgAlpha?:number;
}

export class MiniPayTable extends BaseView {

    private copyiedTarget:PIXI.Sprite;
    private target:PIXI.DisplayObject | undefined;

    private bgGraphic:PIXI.Graphics;
    protected valueText:PIXI.Text;
    private tl:TimelineMax;

    private symbolValues:ISymbolValues;
    private xtraArea:number = 30;

    private currentBetLvl:number = 1;
    private showBetValue:boolean = false;

    private _oriScale:PayTableScaling = {
        portrait: new PIXI.Point(1,1),
        landscape: new PIXI.Point(1,1)
    };

    private DEFAULT_TEXT_STYLE:TextStyleOptions = {
        dropShadow: true,
        dropShadowAngle: 8,
        dropShadowBlur: 10,
        dropShadowDistance: 2,
        fontFamily : "Open Sans",
        fontWeight : FontWeight.BOLD,
        fontSize: 28,
        align:"left",
        wordWrap: true,
        wordWrapWidth: 400,
        fill: [
            "white",
        ]
    };

    constructor(config?:MiniPayTableConfig){
        super();
        if(config){
            if(config.orientation_scale) this._oriScale = config.orientation_scale;
            if(config.style) this.DEFAULT_TEXT_STYLE = config.style;
            if(config.xtraArea != undefined) this.xtraArea = config.xtraArea;
            this.showBetValue = !!config.showBetValue;
        }
    }

    private get oriScale():PIXI.Point{ return ResizeHelper.IsLandscape() ? this._oriScale.landscape : this._oriScale.portrait };

    protected addBaseEventListeners():void{
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.InitDataReceived(event.params[0]));
        EventHandler.addEventListener(this, StageEvent.STAGE_RESIZED, () => this.onResized());
        EventHandler.addEventListener(this, ReelEngineEvent.REEL_RESIZED, () => this.onResized());
        EventHandler.addEventListener(this, PayTableEvent.CLICK, (event:GameEvent) => this.onClicked(event.params[0]));
        EventHandler.addEventListener(this, BalanceEvent.BET, () => this.onNewBet());
        EventHandler.addEventListener(this, BalanceEvent.CURRENT_BET, (event: GameEvent) => this.currentBetLvl = event.params[0]);
    }

    protected onNewBet():void{
        this.target = undefined;
        this.tl = Helper.timeLineKiller(this.tl, new TimelineMax(), true)
    }

    protected InitDataReceived(values:ParsedInitData):void{
        this.symbolValues = values.symbolValues;
        this.createItems();
    }

    protected createItems():void{
        this.bgGraphic = this.addChild(new PIXI.Graphics());

        this.createCopySprite();
        this.createText("x1", this.DEFAULT_TEXT_STYLE);

        StageManager.getLayer(GameConfig.instance.LAYERS.MINI_PAYTABLE.name).addChild(this);
    }

    protected createCopySprite():void{
        this.copyiedTarget = this.addChild(new PIXI.Sprite());
        this.copyiedTarget.anchor.set(0.5,0.5);
    }

    protected createText(text:string, style:TextStyleOptions):void {
        this.valueText = this.addChild(new PIXI.Text("x"+text, style));
        this.valueText.alpha = 0;
    }

    protected onClicked(data:PayTableData):void{

        if(this.defineObjectFromData(data)){

            this.onResized();

            this.animateClick();
        }else{
            this.tl = Helper.timeLineKiller(this.tl, new TimelineMax(), true)
        }
    }

    protected defineObjectFromData(data:PayTableData):boolean{
        this.target = data.dispObj;

        // Copy the targetTexture for display.
        const texture:PIXI.Texture = (<PIXI.Sprite>data.dispObj).texture;

        // Set texture to be displayed.
        this.copyiedTarget.texture = texture;

        // Find the symboltype.
        const sName:string = data.name ? data.name : texture.textureCacheIds[0].split("/")[0];
        return this.setText(sName);
    }

    protected setText(symValueTarget:string ):boolean{
        this.copyiedTarget.alpha = !!this.symbolValues[symValueTarget] ?  1 : 0;

        if(!this.symbolValues[symValueTarget]) return !!this.symbolValues[symValueTarget];
        // Getting Connections.
        const maxConn:string[] = Object.keys(this.symbolValues[symValueTarget]).map((symbolName:string) =>{ return  symbolName;});
        // Getting Connection values.
        const maxConnValues:number[] = ArrayHelper.initArrayWithValues((maxConn.length), (i:number) => { return this.symbolValues[symValueTarget][parseInt(maxConn[i])] } );
        
        this.valueText.text = "";

        if(this.showBetValue)
            maxConn.forEach((element,index) => {
          
                this.valueText.text += element +  "|"+ Currency.format((maxConnValues[index]*this.currentBetLvl)*.05);
            
                if(index+1 != maxConn.length) this.valueText.text += "\n";

            });
        else
            maxConn.forEach((element,index) => {

                this.valueText.text += element +  "|x"+ (maxConnValues[index]*.05).toFixed(2);
                
                if(index+1 != maxConn.length) this.valueText.text += "\n";
            });

        return true;
    }

    protected animateClick():void{
        this.alpha = 1;
        this.tl.fromTo(this.bgGraphic,0.25,{alpha:0},{alpha:1},0);
        this.tl.fromTo(this.valueText,0.25,{alpha:0},{alpha:1},0);
        this.tl.to(this,0.25,{alpha:0}, "+= 1.5");
        this.tl.play();
    }

    protected onResized():void{
        if(this.target != undefined){
            this.tl = Helper.timeLineKiller(this.tl, new TimelineMax(), true)

            this.defineCopiedSprite();

            this.defineText();

            this.defineGraphic();
        }
    }

    protected defineText():void{

        this.valueText.style.fontSize =  this.oriScale.x * <number>this.DEFAULT_TEXT_STYLE.fontSize;
        this.valueText.updateTransform();

        this.valueText.position = this.copyiedTarget.position;

        let targetTxtX2:number = this.copyiedTarget.position.x + this.copyiedTarget.width/2;
        if(this.leftPos ) targetTxtX2 = this.copyiedTarget.position.x - (this.valueText.width +this.copyiedTarget.width/2);

        targetTxtX2 +=  (this.leftPos? -1:1 ) * this.valueText.style.fontSize/10;

        const targetTxtY2:number = this.copyiedTarget.position.y - this.valueText.height/2;
        this.valueText.position.set(targetTxtX2, targetTxtY2);
    }

    protected defineCopiedSprite():void{

        this.copyiedTarget.scale = this.oriScale;
        this.copyiedTarget.position = Helper.copyLocalPosition(this.copyiedTarget,this.target!);

        const pivotOffset:PIXI.Point = MathHelper.pointSub(new PIXI.Point(0.5,0.5), (<PIXI.Sprite>this.target).anchor);
        const pivotPosOffset:PIXI.Point = MathHelper.pointMul(pivotOffset, new PIXI.Point(this.copyiedTarget.width,this.copyiedTarget.height));
        this.copyiedTarget.position = MathHelper.pointAdd(<PIXI.Point>this.copyiedTarget.position ,pivotPosOffset);
    }

    private get boxHeight():number{
        return (this.valueText.height > this.copyiedTarget.height ? this.valueText.height  : this.copyiedTarget.height) + this.xtraArea;
    }

    private get boxWidht():number{
        return this.valueText.width + this.copyiedTarget.width + (this.xtraArea);
    }

    private get closeToBott():boolean{
        return this.boxHeight + Math.round(this.copyiedTarget.position.y) > 720;
    }

    private get leftPos():boolean{
        return this.boxWidht + Math.round(this.copyiedTarget.position.x) > 720;
    }

    private get leftPosOffset():number{
        return (this.leftPos ? 1: -1) * (this.boxWidht/2 - this.copyiedTarget.width/2 - this.xtraArea/2);
    }

    protected defineGraphic():void{
        this.bgGraphic.position = this.copyiedTarget.position;

        this.bgGraphic.clear();
        this.bgGraphic.lineStyle(3, 0xFFFFFF, 1)
        this.bgGraphic.beginFill(0x000000, 0.90);

        this.bgGraphic.drawRoundedRect(
            -this.boxWidht/2,
            -this.boxHeight/2,
            this.boxWidht,
            this.boxHeight,5);
        this.bgGraphic.endFill();
        this.bgGraphic.position.x -= this.leftPosOffset;
    }
}